// WP-30 (V5) — tenant-timezone clock rendering. ONE implementation, shared by
// the Core API, the Worker and the office SPA.
//
// THE DEFECT THIS REPLACES
// ------------------------
// Three call sites rendered clock times as `toISOString().slice(11, 16)` and
// dates as `.slice(0, 10)`:
//   - apps/worker/src/handlers/booking-confirm.handler.ts:176 (the CLIENT email)
//   - apps/api/src/scheduling/booking-confirmation.ts:46 (the O-08 template)
//   - apps/office-web/src/features/scheduling/confirmation-badge.ts:16 (O-07/O-08)
// `toISOString()` is UTC by definition, and `organization.timezone` was loaded
// (apps/worker/src/db/repos.ts:50) and then discarded. So every clock time the
// platform emitted was UTC: a 10:00 SAST booking was stated as 08:00, and a
// booking before 02:00 SAST was dated a day early. US 3.4 AC1 requires the
// client's confirmation to carry the real arrival window.
//
// Everything here takes the tenant's IANA zone EXPLICITLY. There is no
// "current zone" default and no `Z`-flavoured shortcut, because a defaulted
// zone is exactly how the UTC rendering survived three work packages: the
// fixtures were `Z` strings and UTC==SAST was never contradicted.
/**
 * The platform default tenant zone — the same value as the
 * `organization.timezone` column default (migration
 * 20260904100002_wp02_platform_tables/migration.sql:28). Used ONLY where a
 * tenant zone is genuinely unavailable (e.g. the office SPA rehydrating a
 * persisted session saved before this field existed). Never a silent UTC.
 */
export const DEFAULT_TENANT_TIMEZONE = "Africa/Johannesburg";
/** Thrown when a zone string is not a zone Intl can resolve. */
export class InvalidTenantTimezone extends Error {
    timezone;
    constructor(timezone) {
        super(`"${timezone}" is not an IANA time zone this runtime can resolve`);
        this.timezone = timezone;
        this.name = "InvalidTenantTimezone";
    }
}
export function isValidTimezone(timezone) {
    try {
        new Intl.DateTimeFormat("en-ZA", { timeZone: timezone });
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Normalise a possibly-absent/invalid zone to something renderable. Callers
 * that hold a trusted tenant zone should pass it straight through; this exists
 * for the SPA's rehydration edge and for defensive worker payloads.
 */
export function resolveTenantTimezone(timezone) {
    if (typeof timezone === "string" && timezone !== "" && isValidTimezone(timezone))
        return timezone;
    return DEFAULT_TENANT_TIMEZONE;
}
function toDate(instant) {
    const d = instant instanceof Date ? instant : new Date(instant);
    if (Number.isNaN(d.getTime()))
        throw new TypeError(`not a valid instant: ${String(instant)}`);
    return d;
}
function assertZone(timezone) {
    if (!isValidTimezone(timezone))
        throw new InvalidTenantTimezone(timezone);
}
/**
 * The wall-clock parts of `instant` AS SEEN IN `timezone`. This is the single
 * primitive everything else is built from — `formatToParts` is the only way to
 * do this correctly without a tz database of our own.
 */
export function tenantWallClock(instant, timezone) {
    assertZone(timezone);
    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
    }).formatToParts(toDate(instant));
    const get = (type) => {
        const found = parts.find((p) => p.type === type);
        if (!found)
            throw new InvalidTenantTimezone(timezone);
        return Number(found.value);
    };
    return {
        year: get("year"),
        month: get("month"),
        day: get("day"),
        hour: get("hour"),
        minute: get("minute"),
        second: get("second"),
    };
}
const pad = (n) => String(n).padStart(2, "0");
/** `HH:MM` in the tenant's zone — the replacement for `toISOString().slice(11, 16)`. */
export function tenantClockTime(instant, timezone) {
    const w = tenantWallClock(instant, timezone);
    return `${pad(w.hour)}:${pad(w.minute)}`;
}
/** `YYYY-MM-DD` in the tenant's zone — the replacement for `toISOString().slice(0, 10)`. */
export function tenantCalendarDate(instant, timezone) {
    const w = tenantWallClock(instant, timezone);
    return `${w.year}-${pad(w.month)}-${pad(w.day)}`;
}
/** `YYYY-MM-DDTHH:MM` — what an `<input type="datetime-local">` expects, in the TENANT's zone. */
export function tenantLocalInputValue(instant, timezone) {
    return `${tenantCalendarDate(instant, timezone)}T${tenantClockTime(instant, timezone)}`;
}
/**
 * Today's calendar date in the tenant's zone. `new Date().toISOString().slice(0, 10)`
 * rolls over at 02:00 SAST, which is why the calendar's default day was wrong
 * for two hours every night.
 */
export function tenantToday(timezone, now = new Date()) {
    return tenantCalendarDate(now, timezone);
}
/**
 * The UTC offset of `timezone` at `instant`, in minutes east of UTC
 * (Africa/Johannesburg → 120). Derived by differencing the wall clock against
 * the instant, so DST transitions are handled by the ICU database, not by us.
 */
export function tenantOffsetMinutes(instant, timezone) {
    const d = toDate(instant);
    const w = tenantWallClock(d, timezone);
    const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
    // Second-resolution: the ms component is identical on both sides.
    return Math.round((asUtc - (d.getTime() - d.getMilliseconds())) / 60_000);
}
/**
 * The inverse of {@link tenantLocalInputValue}: a `YYYY-MM-DDTHH:MM[:SS]` wall
 * clock the office typed, read AS A TENANT-LOCAL time, returned as an instant.
 *
 * Without this the office's `<input type="datetime-local">` was interpreted in
 * the BROWSER's zone (`new Date(local).toISOString()` in
 * schedule-screen.tsx:25) — correct only while every office user happens to sit
 * in the tenant's zone. Two passes settle the offset at the target instant,
 * which is what makes DST boundaries land on the right side.
 */
export function instantFromTenantLocal(localValue, timezone) {
    assertZone(timezone);
    const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(localValue.trim());
    if (!m)
        throw new TypeError(`not a YYYY-MM-DDTHH:MM local value: ${localValue}`);
    const [, y, mo, d, h, mi, s] = m;
    const naive = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? 0));
    let guess = new Date(naive - tenantOffsetMinutes(naive, timezone) * 60_000);
    // Re-settle once: the first offset was sampled at the naive instant, which
    // can sit on the far side of a DST change from the real one.
    guess = new Date(naive - tenantOffsetMinutes(guess, timezone) * 60_000);
    return guess;
}
/**
 * The UTC instant range covering a `YYYY-MM-DD` day IN THE TENANT'S ZONE,
 * half-open `[from, to)`.
 *
 * WP-31: promoted out of `apps/office-web/src/i18n/tenant-time.ts` (where WP-30
 * left it as `tenantDayRange`) so the FIELD app's F-01 day filter and the
 * office calendar derive their day boundaries from one implementation. The
 * field app needs exactly this: `date(slot_start)` in SQLite is the UTC day, so
 * "today's jobs" were a UTC day on a phone showing SAST times.
 *
 * `+36h then re-floor` rather than `+24h`: across a DST transition those
 * differ, and the day the caller is looking at must be whole.
 */
export function tenantDayBounds(day, timezone) {
    const from = instantFromTenantLocal(`${day}T00:00`, timezone);
    const nextDay = tenantToday(timezone, new Date(from.getTime() + 36 * 60 * 60 * 1000));
    return { from, to: instantFromTenantLocal(`${nextDay}T00:00`, timezone) };
}
/** `tenantDayBounds` as ISO strings — what a store or a query wants. */
export function tenantDayBoundsIso(day, timezone) {
    const { from, to } = tenantDayBounds(day, timezone);
    return { from: from.toISOString(), to: to.toISOString() };
}
/**
 * `day` shifted by whole TENANT days (`-1` = yesterday, `+1` = tomorrow). Not
 * `±86 400 000 ms`: across a DST change that lands on the wrong calendar day,
 * which is the whole reason the office calendar's own day maths moved here.
 */
export function shiftTenantDay(day, timezone, deltaDays) {
    let cursor = day;
    const step = deltaDays >= 0 ? 1 : -1;
    for (let i = 0; i < Math.abs(deltaDays); i += 1) {
        // Hop from MIDDAY, not from midnight. Midday +/- 24 h lands in the
        // adjacent day for any real-world DST shift (at most +/- 2 h), whereas
        // midnight +/- 36 h overshoots backwards into the day before last. That
        // was a real bug in the first cut of this function, caught by
        // `test/check-in-window.test.mjs` rather than by a foreman.
        const midday = instantFromTenantLocal(`${cursor}T12:00`, timezone);
        cursor = tenantCalendarDate(new Date(midday.getTime() + step * 86_400_000), timezone);
    }
    return cursor;
}
/** `24 Sep, 10:00` — a human date+time in the tenant's zone. */
export function formatTenantDateTime(instant, timezone, options = {}) {
    assertZone(timezone);
    return new Intl.DateTimeFormat(options.locale ?? "en-ZA", {
        timeZone: timezone,
        day: "2-digit",
        month: "short",
        ...(options.withYear ? { year: "numeric" } : {}),
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    }).format(toDate(instant));
}
/** `24 Sep` — a human date in the tenant's zone. */
export function formatTenantDate(instant, timezone, options = {}) {
    assertZone(timezone);
    return new Intl.DateTimeFormat(options.locale ?? "en-ZA", {
        timeZone: timezone,
        day: "2-digit",
        month: "short",
        ...(options.withYear ? { year: "numeric" } : {}),
    }).format(toDate(instant));
}
/**
 * The zone's short name at that instant, for client-facing copy — `SAST` for
 * Africa/Johannesburg under the platform's `en-ZA` default. A confirmation that
 * says "10:00" without saying WHICH 10:00 is the same defect one step removed,
 * so every client-facing time carries this.
 *
 * The short name is locale-sensitive in ICU (`en-US` renders SAST as `GMT+2`),
 * hence the explicit `locale` argument defaulting to the platform's `en-ZA`
 * (`organization.locale`). Zones with no short name in the locale fall back to
 * a `GMT+N` form, which is still unambiguous — never a bare, unlabelled clock.
 */
export function tenantTimezoneAbbreviation(instant, timezone, locale = "en-ZA") {
    assertZone(timezone);
    const parts = new Intl.DateTimeFormat(locale, {
        timeZone: timezone,
        timeZoneName: "short",
        hour: "2-digit",
    }).formatToParts(toDate(instant));
    return parts.find((p) => p.type === "timeZoneName")?.value ?? timezone;
}
//# sourceMappingURL=tenant-time.js.map